import type {
  RyftDirectorDocumentUploadRequestType,
  RyftDirectorDocumentUploadResponseType,
} from "@sellar-io/api-v2/types";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useFirebaseUser } from "../../../useFirebaseUser.shared";
import { useSellarCurrentBusiness } from "../../../useSellarCurrentBusiness.shared";
import { apiV2AssertResponse, apiV2Request } from "../apiV2Request";
import { ServerStateKeys } from "../ServerStateKeys";

type RyftDirectorDocumentUploadParameters =
  RyftDirectorDocumentUploadRequestType & {
    directorId: string;
    front: File;
    back?: File;
  };

export const useRyftManagementDirectorDocumentUpload = () => {
  const queryClient = useQueryClient();
  const { business } = useSellarCurrentBusiness();
  const { user } = useFirebaseUser();

  return useMutation({
    mutationFn: ({
      directorId,
      front,
      back,
      documentType,
    }: RyftDirectorDocumentUploadParameters) => {
      const data = new FormData();
      data.append("front", front);
      if (back) {
        data.append("back", back);
      }
      data.append("documentType", documentType);

      return apiV2Request({
        user,
        business,
        method: "post",
        path: `ryft-management/accounts/directors/${directorId}/documents`,
        data,
      }).then(
        (response) =>
          apiV2AssertResponse<RyftDirectorDocumentUploadResponseType>(response)
            .data,
      );
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: [
          user?.uid,
          business?.id,
          ServerStateKeys.RyftManagementAccount,
        ],
      });
    },
  });
};
